/**
 * Pure-TS primitives for classical court-line detection:
 *   - Sobel magnitude  -> binary edge map
 *   - Hough transform  -> peak (rho, theta) lines in image space
 *   - Line intersection in normal-form
 *
 * These helpers are intentionally DOM-free so they can also run in a worker
 * or a Node-side server. The browser-side `AutoCourtCalibrator` wraps them
 * with an OffscreenCanvas-based downscaler + a color-surface mask.
 */

/**
 * A straight line in normal form: `x·cos(θ) + y·sin(θ) = ρ`.
 * `theta` is in radians in [0, π) and `rho` can be negative for lines that
 * don't contain the origin. `votes` is the Hough accumulator value (strength).
 */
export interface HoughLine {
  readonly rho: number;
  readonly theta: number;
  readonly votes: number;
}

/**
 * Grayscale image on a contiguous row-major Uint8 buffer.
 * `data[y*width + x]` is the intensity in [0, 255].
 */
export interface GrayImage {
  readonly width: number;
  readonly height: number;
  readonly data: Uint8ClampedArray;
}

/**
 * Binary edge map (0 or 255) in the same layout as `GrayImage`.
 */
export type EdgeMap = GrayImage;

/**
 * Gaussian 5-tap blur (approx σ ≈ 1). Separable, in-place-ish via two passes.
 * Dead simple kernel: [1, 4, 6, 4, 1] / 16.
 *
 * We pre-allocate an intermediate buffer so callers can reuse it — the
 * calibrator runs ONCE per session, so allocations are not critical, but
 * keeping this pure is still nice.
 */
export function gaussianBlur5(src: GrayImage): GrayImage {
  const { width: w, height: h, data } = src;
  const tmp = new Uint8ClampedArray(w * h);
  const out = new Uint8ClampedArray(w * h);
  const k0 = 1, k1 = 4, k2 = 6, k3 = 4, k4 = 1;
  const kSum = 16;

  // Horizontal pass (src -> tmp).
  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) {
      const x0 = clamp(x - 2, 0, w - 1);
      const x1 = clamp(x - 1, 0, w - 1);
      const x2 = x;
      const x3 = clamp(x + 1, 0, w - 1);
      const x4 = clamp(x + 2, 0, w - 1);
      const v =
        k0 * data[row + x0]! +
        k1 * data[row + x1]! +
        k2 * data[row + x2]! +
        k3 * data[row + x3]! +
        k4 * data[row + x4]!;
      tmp[row + x] = v / kSum;
    }
  }
  // Vertical pass (tmp -> out).
  for (let y = 0; y < h; y++) {
    const y0 = clamp(y - 2, 0, h - 1) * w;
    const y1 = clamp(y - 1, 0, h - 1) * w;
    const y2 = y * w;
    const y3 = clamp(y + 1, 0, h - 1) * w;
    const y4 = clamp(y + 2, 0, h - 1) * w;
    for (let x = 0; x < w; x++) {
      const v =
        k0 * tmp[y0 + x]! +
        k1 * tmp[y1 + x]! +
        k2 * tmp[y2 + x]! +
        k3 * tmp[y3 + x]! +
        k4 * tmp[y4 + x]!;
      out[y2 + x] = v / kSum;
    }
  }
  return { width: w, height: h, data: out };
}

/**
 * 3x3 Sobel operator on a grayscale image. Returns a binary edge map where
 * pixels with magnitude ≥ `threshold` (0..255) are set to 255.
 *
 * We approximate magnitude by `|gx| + |gy|` (cheap, typical for Hough input).
 */
export function sobelEdges(src: GrayImage, threshold: number): EdgeMap {
  const { width: w, height: h, data } = src;
  const out = new Uint8ClampedArray(w * h);
  // Skip 1-px border.
  for (let y = 1; y < h - 1; y++) {
    const ym = (y - 1) * w;
    const y0 = y * w;
    const yp = (y + 1) * w;
    for (let x = 1; x < w - 1; x++) {
      const a = data[ym + x - 1]!, b = data[ym + x]!, c = data[ym + x + 1]!;
      const d = data[y0 + x - 1]!, /* e */ f = data[y0 + x + 1]!;
      const g = data[yp + x - 1]!, hh = data[yp + x]!, i = data[yp + x + 1]!;
      const gx = -a + c - 2 * d + 2 * f - g + i;
      const gy = -a - 2 * b - c + g + 2 * hh + i;
      const mag = Math.abs(gx) + Math.abs(gy);
      // Normalise: max |gx|+|gy| ≈ 8*255 for a step edge; divide by 4 to fit
      // in 0..510 then clamp. Using a crude shift is good enough here.
      const m = mag > 1020 ? 255 : mag >> 2;
      out[y0 + x] = m >= threshold ? 255 : 0;
    }
  }
  return { width: w, height: h, data: out };
}

/**
 * Hough transform over a binary edge map. θ is binned every `thetaStepDeg`
 * degrees in [0, 180). ρ is binned per pixel. Returns the top `maxLines`
 * lines with vote counts ≥ `voteThreshold`, after non-maximum suppression
 * in the (rho, theta) accumulator.
 *
 * Complexity: O(edgePixels * numThetaBins). For a 640x360 edge map with
 * ~5k edge pixels and 180 bins that's < 1M ops — sub-ms in JS.
 */
export function houghLines(
  edges: EdgeMap,
  opts: {
    voteThreshold: number;
    maxLines?: number;
    thetaStepDeg?: number;
    /** Suppression window half-size in (rho, theta) bins. Default 8,2. */
    nmsRho?: number;
    nmsTheta?: number;
  },
): HoughLine[] {
  const w = edges.width;
  const h = edges.height;
  const data = edges.data;
  const thetaStepDeg = opts.thetaStepDeg ?? 1;
  const maxLines = opts.maxLines ?? 40;
  const nmsRho = opts.nmsRho ?? 8;
  const nmsTheta = opts.nmsTheta ?? 2;

  const numTheta = Math.round(180 / thetaStepDeg);
  const diag = Math.ceil(Math.hypot(w, h));
  const numRho = 2 * diag + 1;
  const rhoOffset = diag; // so rho-bin = floor(rho) + diag, always ≥ 0

  // Precompute sin/cos per theta bin.
  const cosT = new Float32Array(numTheta);
  const sinT = new Float32Array(numTheta);
  for (let t = 0; t < numTheta; t++) {
    const a = (t * thetaStepDeg * Math.PI) / 180;
    cosT[t] = Math.cos(a);
    sinT[t] = Math.sin(a);
  }

  const acc = new Int32Array(numRho * numTheta);

  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) {
      if (data[row + x]! === 0) continue;
      for (let t = 0; t < numTheta; t++) {
        const rho = x * cosT[t]! + y * sinT[t]!;
        const r = Math.round(rho) + rhoOffset;
        if (r < 0 || r >= numRho) continue;
        acc[r * numTheta + t]! += 1;
      }
    }
  }

  // Collect peaks above threshold then sort by votes desc.
  type Peak = { r: number; t: number; v: number };
  const peaks: Peak[] = [];
  for (let r = 0; r < numRho; r++) {
    for (let t = 0; t < numTheta; t++) {
      const v = acc[r * numTheta + t]!;
      if (v >= opts.voteThreshold) peaks.push({ r, t, v });
    }
  }
  peaks.sort((a, b) => b.v - a.v);

  // Non-maximum suppression: keep a peak only if no stronger peak has been
  // kept within (nmsRho, nmsTheta) bins.
  const kept: Peak[] = [];
  for (const p of peaks) {
    let suppressed = false;
    for (const q of kept) {
      if (Math.abs(q.r - p.r) <= nmsRho && circDist(q.t, p.t, numTheta) <= nmsTheta) {
        suppressed = true;
        break;
      }
    }
    if (!suppressed) {
      kept.push(p);
      if (kept.length >= maxLines) break;
    }
  }

  return kept.map((p) => ({
    rho: p.r - rhoOffset,
    theta: (p.t * thetaStepDeg * Math.PI) / 180,
    votes: p.v,
  }));
}

/**
 * Intersection of two lines in normal form. Returns `null` when the lines
 * are (near-)parallel — the determinant of the 2x2 system is close to zero.
 */
export function lineIntersect(a: HoughLine, b: HoughLine): [number, number] | null {
  // [cos(θa) sin(θa)] [x]   [ρa]
  // [cos(θb) sin(θb)] [y] = [ρb]
  const ca = Math.cos(a.theta), sa = Math.sin(a.theta);
  const cb = Math.cos(b.theta), sb = Math.sin(b.theta);
  const det = ca * sb - sa * cb;
  if (Math.abs(det) < 1e-6) return null;
  const x = (a.rho * sb - b.rho * sa) / det;
  const y = (b.rho * ca - a.rho * cb) / det;
  return [x, y];
}

/**
 * Convert an RGBA image (row-major, Canvas-style) to grayscale using the
 * Rec. 601 luma coefficients. `rgba.length` must equal `width*height*4`.
 */
export function rgbaToGray(
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
): GrayImage {
  const out = new Uint8ClampedArray(width * height);
  for (let i = 0, j = 0; i < rgba.length; i += 4, j++) {
    out[j] = (0.299 * rgba[i]! + 0.587 * rgba[i + 1]! + 0.114 * rgba[i + 2]!) | 0;
  }
  return { width, height, data: out };
}

/**
 * Zero out every edge pixel whose RGB color is NOT a plausible tennis-court
 * surface. Heuristic: a pixel is court-ish when its saturation is low-to-
 * moderate (hard courts are desaturated blue/green) OR its hue falls in the
 * blue/green/red/orange bands typical of hard/grass/clay surfaces.
 *
 * This is intentionally lenient — false negatives (losing a real court edge)
 * kill calibration entirely, while false positives (keeping a crowd edge)
 * only add noise that Hough voting filters out.
 */
export function applyCourtColorMask(
  edges: EdgeMap,
  rgba: Uint8ClampedArray,
): EdgeMap {
  const { width: w, height: h } = edges;
  const out = new Uint8ClampedArray(w * h);
  for (let i = 0, j = 0; j < w * h; i += 4, j++) {
    if (edges.data[j]! === 0) continue;
    const r = rgba[i]!, g = rgba[i + 1]!, b = rgba[i + 2]!;
    if (isCourtish(r, g, b)) out[j] = 255;
  }
  return { width: w, height: h, data: out };
}

function isCourtish(r: number, g: number, b: number): boolean {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  // Very dark pixels (shadow/crowd) — reject.
  if (max < 30) return false;
  // Very white pixels (the lines themselves) — keep regardless of hue.
  if (min > 180 && delta < 40) return true;
  const s = max === 0 ? 0 : delta / max;
  // Low saturation: often concrete/hard or painted surface. Keep.
  if (s < 0.55) return true;
  // Hue in degrees via the standard HSV formula.
  let hDeg: number;
  if (delta === 0) hDeg = 0;
  else if (max === r) hDeg = 60 * (((g - b) / delta) % 6);
  else if (max === g) hDeg = 60 * ((b - r) / delta + 2);
  else hDeg = 60 * ((r - g) / delta + 4);
  if (hDeg < 0) hDeg += 360;
  // Blue hard courts: 180..260. Green grass/hard: 80..170. Red/orange clay: 0..40 or 330..360.
  if (hDeg >= 180 && hDeg <= 260) return true;
  if (hDeg >= 80 && hDeg <= 170) return true;
  if (hDeg <= 40 || hDeg >= 330) return true;
  return false;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function circDist(a: number, b: number, period: number): number {
  const d = Math.abs(a - b);
  return d > period / 2 ? period - d : d;
}
